import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css } from '@emotion/react';
import {
  animations,
  colors,
  mixins,
  text,
} from '../../../core/styles/theme/default';
import ErrorMessage from '../ErrorMessage';

const StyledLabel = styled.label`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
`;

const StyledSpan = styled.span(
  ({
    isRequired,
    hideLabel,
  }: {
    isRequired?: boolean;
    hideLabel?: boolean;
  }) => {
    const requiredStyles = css`
      &:after {
        content: '*';
        ${text.p4};
        display: inline-block;
        margin-left: ${rem(4)};
        color: ${colors.nightBlue500};
      }
    `;

    return css`
      ${text.p4};
      padding: 0 ${rem(2)};
      color: ${colors.nightBlue500};
      background-color: ${colors.white};
      pointer-events: none;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: calc(100% - 2 * ${rem(16)});
      overflow: hidden;
      padding-bottom: ${rem(4)};

      ${isRequired && requiredStyles}
      ${hideLabel &&
      css`
        ${mixins.visuallyHidden};
      `}
    `;
  },
);

const StyledInput = styled.input(({ hasError }: { hasError: boolean }) => {
  const FloatedLabelStyle = css`
    ${text.l1};
    top: 0;
  `;

  return css`
    ${text.p3};
    display: block;
    width: 100%;
    height: ${rem(56)};
    padding: 0 ${rem(16)};
    border: ${rem(1)} solid ${colors.grue300};
    border-radius: ${rem(2)};
    color: ${colors.grey900};
    background-color: ${colors.white};
    outline: none;
    text-overflow: ellipsis;
    transition: all ${animations.default};
    ${hasError && `border-color: ${colors.error}`};

    &:disabled {
      background-color: ${colors.grey300};

      & + span {
        background-color: ${colors.grey300};
      }
    }

    &:not(:-ms-input-placeholder) {
      & + span {
        ${FloatedLabelStyle}
      }
    }
    &:not(:placeholder-shown) {
      & + span {
        ${FloatedLabelStyle}
      }
    }

    &:focus {
      text-overflow: unset;
      box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06),
        0px 4px 6px rgba(0, 0, 0, 0.1);
    }
  `;
});

const Input = ({
  label,
  id,
  name,
  value,
  placeholder,
  className,
  hideLabel,
  errorText,
  onChangeRequest,
  onFocusRequest,
  required: isRequired,
  ...rest
}: InputProps) => {
  const [inputVal, setInputVal] = useState(value || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputVal(event.target.value);
    onChangeRequest?.(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocusRequest?.(event);
  };

  useEffect(() => {
    setInputVal(value ?? '');
  }, [value]);

  return (
    <StyledLabel className={className} htmlFor={id ?? name}>
      <StyledSpan hideLabel={hideLabel} isRequired={isRequired}>
        {label}
      </StyledSpan>
      <StyledInput
        id={id ?? name}
        value={inputVal}
        name={name}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        hasError={!!errorText}
        {...rest}
      />
      {errorText && <ErrorMessage text={errorText} />}
    </StyledLabel>
  );
};

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Label of the Input */
  label: string;
  /** Id of the Input */
  id?: string;
  /** If label should be hidden */
  hideLabel?: boolean;
  /** Error Text to be shown */
  errorText?: string;
  /** Placeholder to be shown */
  placeholder?: string;
  /** Optional callback fired when the Input changes. For example usefull to get the value of the Input */
  onChangeRequest?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Optional callback fired when the Input gets focus */
  onFocusRequest?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

export default Input;
